import { ClockIcon } from '@heroicons/react/24/outline'
import { ReactElement } from 'react'
import { SectionTitle } from 'app/components/ai-agent-voice-ops/SectionTitle'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import SegmentControl from 'app/components/viz/SegmentControl'
import { DataSourceBadge } from './DataSourceBadge'
import { ListErrorState } from './ListErrorState'
import { REPLY_WAIT_LABEL, WAIT_STATUSES, WAIT_STATUS_LABEL } from './smsReplyWaits'
import type { UseSmsAgentOpsReturn, WaitFilter } from './useSmsAgentOps'

const FILTER_OPTIONS: { value: WaitFilter; label: string }[] = [
  { value: `all`, label: `All` },
  ...WAIT_STATUSES.map((status) => ({ value: status, label: WAIT_STATUS_LABEL[status] })),
]

type ReplyWaitsSectionProps = Pick<
  UseSmsAgentOpsReturn,
  `listErrors` | `replyWaits` | `waitCounts` | `waitFilter` | `setWaitFilter` | `replyWaitColumns` | `replyWaitsRef`
>

/** Waiting on a Reply (plan D37): patients on AI threads who waited more than the threshold for a reply. */
export function ReplyWaitsSection({
  listErrors,
  replyWaits,
  waitCounts,
  waitFilter,
  setWaitFilter,
  replyWaitColumns,
  replyWaitsRef,
}: ReplyWaitsSectionProps): ReactElement {
  const error = listErrors.waiting ?? listErrors.replies

  return (
    <div ref={replyWaitsRef} className="mb-8 scroll-mt-4">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-y-2">
        <SectionTitle
          title="Waiting on a Reply"
          badge={`${waitCounts.all} Patients`}
          adornment={<DataSourceBadge source="live" />}
        />
        {/* Scrolls on its own on a phone rather than pushing the page sideways. */}
        <div className="max-w-full overflow-x-auto">
          <SegmentControl
            label="Status"
            buttons={FILTER_OPTIONS.map((option) => ({
              label: `${option.label} ${waitCounts[option.value]}`,
              onClick: () => setWaitFilter(option.value),
              isActive: waitFilter === option.value,
            }))}
          />
        </div>
      </div>
      <p className="mb-3 text-body-sm text-subtle">
        {`Patients on AI threads who waited more than ${REPLY_WAIT_LABEL} for a reply. Waiting: still no reply, right now, whatever the date range. Replied Late: the AI answered late in the date range. Open the contact or the thread to take over.`}
      </p>
      <SimpleTable
        activeSubpage="sms-agent-ops-reply-waits"
        data={replyWaits}
        columns={replyWaitColumns}
        enableDeepLinking={false}
        hideFooter
        emptyState={
          error ? (
            <ListErrorState subject="AI replies" />
          ) : (
            <TableEmptyState
              icon={<ClockIcon className="h-6 w-6" />}
              title="Nobody waited on the AI"
              description={`Every patient on an AI thread got a reply within ${REPLY_WAIT_LABEL}.`}
            />
          )
        }
      />
    </div>
  )
}
